import {
  isStakeholderContactPerson,
  type AddMultipleStakeholderContactsRequestPayload,
  type StakeholderContact,
  type StakeholderContactType,
  type StakeholderType
} from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'

/**
 * Read a contact-type-specific field off either union variant: missing on
 * this variant, or null, reads as null.
 */
export function contactFieldOrNull(
  contact: StakeholderContact | undefined,
  field: string
): string | null {
  if (!contact) return null
  const value = (contact as unknown as Record<string, unknown>)[field]
  return typeof value === 'string' ? value : null
}

/**
 * Seed a fully-populated `StakeholderContact` from a (possibly partial)
 * existing contact plus the owning stakeholder's info. Fresh contacts get a
 * generated id.
 */
export function buildInitialContact(params: {
  contact: StakeholderContact | undefined
  contactType: StakeholderContactType
  stakeholderInfo: {
    stakeholderId: string
    stakeholderName: string
    stakeholderShortName: string
    stakeholderType: StakeholderType
  }
}): StakeholderContact {
  return {
    id: params.contact?.id ?? globalUtils.functions.uuid(),
    stakeholderId: params.stakeholderInfo.stakeholderId,
    stakeholderName: params.stakeholderInfo.stakeholderName,
    stakeholderShortName: params.stakeholderInfo.stakeholderShortName,
    stakeholderType: params.stakeholderInfo.stakeholderType,
    contactType: params.contactType,
    isInactive: false,
    otherName: contactFieldOrNull(params.contact, 'otherName'),
    email: params.contact?.email ?? null,
    directLine: params.contact?.directLine ?? null,
    mobilePhone: params.contact?.mobilePhone ?? null,
    firstName: contactFieldOrNull(params.contact, 'firstName'),
    lastName: contactFieldOrNull(params.contact, 'lastName'),
    jobTitle: contactFieldOrNull(params.contact, 'jobTitle'),
    department: contactFieldOrNull(params.contact, 'department')
  }
}

/** Sort contacts: persons before others, then alphabetically by name. */
export function sortContacts(
  contacts: StakeholderContact[]
): StakeholderContact[] {
  return contacts.sort((a, b) => {
    // First, sort by contactType (person comes before other)
    if (a.contactType !== b.contactType) {
      return a.contactType === 'person' ? -1 : 1
    }

    // If same contactType, sort by the appropriate name field
    const nameA = isStakeholderContactPerson(a)
      ? (a.firstName ?? '')
      : (a.otherName ?? '')
    const nameB = isStakeholderContactPerson(b)
      ? (b.firstName ?? '')
      : (b.otherName ?? '')
    return nameA.localeCompare(nameB)
  })
}

/** Map locally-collected contacts into a batch-add request payload. */
export function mapContactsToBatchPayload(
  stakeholderId: string,
  contacts: StakeholderContact[]
): AddMultipleStakeholderContactsRequestPayload {
  return {
    stakeholderId,
    contacts: contacts.map((contact) => {
      if (isStakeholderContactPerson(contact)) {
        return {
          id: contact.id,
          contactType: 'person',
          stakeholderId: contact.stakeholderId,
          stakeholderName: contact.stakeholderName,
          stakeholderShortName: contact.stakeholderShortName,
          stakeholderType: contact.stakeholderType,
          firstName: contact.firstName,
          lastName: contact.lastName,
          jobTitle: contact.jobTitle,
          email: contact.email,
          directLine: contact.directLine ?? null,
          mobilePhone: contact.mobilePhone ?? null,
          department: contact.department ?? null,
          isInactive: false
        }
      }
      return {
        id: contact.id,
        contactType: 'other',
        stakeholderId: contact.stakeholderId,
        stakeholderName: contact.stakeholderName,
        stakeholderShortName: contact.stakeholderShortName,
        stakeholderType: contact.stakeholderType,
        otherName: contact.otherName,
        email: contact.email,
        directLine: contact.directLine ?? null,
        mobilePhone: contact.mobilePhone ?? null,
        isInactive: false
      }
    })
  }
}
